import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError, formatCOP } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Disclaimer } from "../components/Disclaimer";
import { UnspscPicker } from "../components/UnspscPicker";
import { DEPARTAMENTOS } from "../lib/departamentos";
import { toast } from "sonner";

const MIN_TEXT = 30;

const GOODS_TYPES = [
  { v: "bienes", l: "Bienes" },
  { v: "servicios", l: "Servicios" },
  { v: "obra_publica", l: "Obra pública" },
  { v: "consultoria", l: "Consultoría" },
  { v: "interventoria", l: "Interventoría" },
  { v: "mixto", l: "Mixto" },
];
const MODALIDADES = [
  { v: "no_definida", l: "No definida" },
  { v: "licitacion_publica", l: "Licitación pública" },
  { v: "seleccion_abreviada", l: "Selección abreviada" },
  { v: "concurso_meritos", l: "Concurso de méritos" },
  { v: "contratacion_directa", l: "Contratación directa" },
  { v: "minima_cuantia", l: "Mínima cuantía" },
  { v: "app", l: "APP / Asociación público privada" },
];
const ENTITY_TYPES = [
  { v: "nacional", l: "Nacional" },
  { v: "territorial_municipal", l: "Territorial municipal" },
  { v: "territorial_departamental", l: "Territorial departamental" },
  { v: "descentralizada", l: "Descentralizada" },
  { v: "empresa_industrial_comercial_estado", l: "Empresa industrial y comercial del Estado" },
  { v: "otra", l: "Otra" },
];
const FUNDING = [
  { v: "presupuesto_nacional", l: "Presupuesto nacional" },
  { v: "sgp", l: "Sistema General de Participaciones" },
  { v: "sgr", l: "Sistema General de Regalías" },
  { v: "recursos_propios", l: "Recursos propios" },
  { v: "credito", l: "Crédito" },
  { v: "cooperacion_donacion", l: "Cooperación / Donación" },
  { v: "mixto", l: "Mixto" },
  { v: "otro", l: "Otro" },
];
const URGENCIAS = [
  { v: "normal", l: "Normal" },
  { v: "alta", l: "Alta" },
  { v: "critica", l: "Crítica" },
];

function SectionHeader({ n, title, subtitle }) {
  return (
    <div className="mb-5 pb-3 border-b border-gray-200">
      <p className="text-[11px] uppercase tracking-[0.22em] text-primary font-medium mb-1">
        Sección {n}
      </p>
      <h2 className="font-serif text-xl text-gray-900">{title}</h2>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
}

export default function NewCasePage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    need_description: "",
    object_description: "",
    estimated_amount_cop: "",
    goods_services_works_type: "",
    expected_term_months: "",
    deadline_date: "",
    urgencia: "normal",
    execution_location: "",
    execution_department: "",
    entity_type: "",
    entity_name: "",
    funding_source: "",
    expected_modality: "no_definida",
    unspsc_codes: [],
    similar_process_links: "",
    riesgos_percibidos: "",
    consent_terms: false,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valorNum = Number(form.estimated_amount_cop);
  const needLen = form.need_description.trim().length;
  const objLen = form.object_description.trim().length;
  const canSubmit =
    form.title.trim().length >= 2 &&
    needLen >= MIN_TEXT &&
    objLen >= MIN_TEXT &&
    !Number.isNaN(valorNum) &&
    valorNum > 0 &&
    form.goods_services_works_type &&
    form.entity_type &&
    form.entity_name.trim().length >= 2 &&
    form.funding_source &&
    form.consent_terms;

  const _buildBody = () => {
    const unspsc = Array.isArray(form.unspsc_codes) ? form.unspsc_codes : [];
    const links = form.similar_process_links
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    return {
      title: form.title.trim(),
      objeto_contractual: form.object_description.trim(),
      valor_estimado_cop: valorNum,
      need_description: form.need_description.trim(),
      object_description: form.object_description.trim(),
      estimated_amount_cop: valorNum,
      goods_services_works_type: form.goods_services_works_type || null,
      expected_term_months: form.expected_term_months ? Number(form.expected_term_months) : null,
      execution_location: form.execution_location.trim() || null,
      execution_department: form.execution_department || null,
      entity_type: form.entity_type || null,
      entity_name: form.entity_name.trim() || null,
      entity_is_national: form.entity_type === "nacional",
      funding_source: form.funding_source || null,
      expected_modality: form.expected_modality || "no_definida",
      unspsc_codes: unspsc,
      similar_process_links: links,
      deadline_date: form.deadline_date || null,
      urgencia: form.urgencia,
      riesgos_percibidos: form.riesgos_percibidos.trim() || null,
      consent_terms: true,
    };
  };

  const onSaveDraft = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!canSubmit) {
      setError("Faltan campos mínimos. Revisa la marca ✱ en cada sección.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: created } = await api.post("/cases", _buildBody());
      toast.success("Borrador del precontrato guardado.");
      // Sin prevalidar: vamos al detalle del caso para que el user decida cuándo prevalidar
      navigate(`/cases/${created.id}`);
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || "No se pudo guardar el borrador";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!canSubmit) {
      setError("Faltan campos mínimos. Revisa la marca ✱ en cada sección.");
      return;
    }
    setSubmitting(true);
    try {
      // Crea el caso (versión 1 del borrador)
      const { data: created } = await api.post("/cases", _buildBody());

      toast.success("Caso creado. Solicitando prevalidación gratuita…");

      // Ejecuta la prevalidación automática
      const { data: result } = await api.post(`/cases/${created.id}/submit-intake`);
      const r = result?.prevalidation?.result;
      if (r === "apto") toast.success("Tu caso es APTO para Diagnóstico.");
      else if (r === "posiblemente_apto") toast.message("Prevalidación requiere revisión — revisa el detalle.");
      else if (r === "no_apto") toast.warning("Caso no apto para diagnóstico automático.");
      else if (r === "out_of_scope") toast.warning("Caso fuera de alcance automático.");
      else toast.message("Resultado disponible.");

      navigate(`/cases/${created.id}/prevalidation`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = formatApiError(detail) || "No se pudo procesar la solicitud";
      if (status === 429) {
        setError(`Has superado el límite de prevalidaciones por hora. ${msg}`);
      } else {
        setError(msg);
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="new-case-page" className="container-prose py-12">
      <div className="max-w-3xl mx-auto">
        <div className="mb-2">
          <Link to="/dashboard" data-testid="new-case-back" className="text-sm text-gray-500 hover:text-primary">
            ← Volver al panel
          </Link>
        </div>
        <div className="mb-10 pb-6 border-b border-gray-200">
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">
            Prevalidación gratuita
          </p>
          <h1 className="font-serif text-3xl text-gray-900 mb-2">
            Solicita la prevalidación de tu caso
          </h1>
          <p className="text-sm text-gray-600">
            Completa la información del proceso. Nuestra IA hará una clasificación preliminar
            del caso y, si aplica, te ofrecerá avanzar al Diagnóstico (Diagnóstico Precontractual revisado por experto humano).
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-10"
          data-testid="new-case-form"
        >
          {/* Sección 0 — Título corto */}
          <section className="border border-gray-200 bg-white p-7">
            <SectionHeader n="0" title="Identificación" subtitle="Un título corto para identificar tu caso." />
            <div className="space-y-1.5">
              <Label htmlFor="f-title">Título del caso <span className="text-destructive">✱</span></Label>
              <Input
                id="f-title"
                required
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                data-testid="new-case-title-input"
                className="rounded-sm h-11"
                placeholder="Ej. Adquisición de equipos médicos — hospital regional"
              />
            </div>
          </section>

          {/* Sección 1 — Necesidad y objeto */}
          <section className="border border-gray-200 bg-white p-7">
            <SectionHeader n="1" title="Necesidad y objeto" subtitle="Mínimo 30 caracteres en cada campo." />

            <div className="space-y-1.5 mb-6">
              <Label htmlFor="f-need">
                Descripción de la necesidad <span className="text-destructive">✱</span>
                <span className="ml-2 text-[11px] text-gray-500">
                  {needLen}/30 mín
                </span>
              </Label>
              <Textarea
                id="f-need"
                required
                rows={4}
                value={form.need_description}
                onChange={(e) => setField("need_description", e.target.value)}
                data-testid="new-case-need-input"
                className="rounded-sm"
                placeholder="¿Por qué necesita la entidad este contrato? ¿Qué problema o función misional resuelve?"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="f-obj">
                Objeto contractual <span className="text-destructive">✱</span>
                <span className="ml-2 text-[11px] text-gray-500">{objLen}/30 mín</span>
              </Label>
              <Textarea
                id="f-obj"
                required
                rows={6}
                value={form.object_description}
                onChange={(e) => setField("object_description", e.target.value)}
                data-testid="new-case-objeto-input"
                className="rounded-sm"
                placeholder="Describe el objeto del contrato: alcance, productos o servicios, entregables…"
              />
            </div>
          </section>

          {/* Sección 2 — Clasificación */}
          <section className="border border-gray-200 bg-white p-7">
            <SectionHeader n="2" title="Clasificación" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label>Tipo de bien/servicio/obra <span className="text-destructive">✱</span></Label>
                <Select
                  value={form.goods_services_works_type}
                  onValueChange={(v) => setField("goods_services_works_type", v)}
                >
                  <SelectTrigger data-testid="new-case-goods-type" className="rounded-sm h-11">
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {GOODS_TYPES.map((o) => (
                      <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Modalidad propuesta</Label>
                <Select
                  value={form.expected_modality}
                  onValueChange={(v) => setField("expected_modality", v)}
                >
                  <SelectTrigger data-testid="new-case-modality" className="rounded-sm h-11">
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {MODALIDADES.map((o) => (
                      <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Tipo de entidad <span className="text-destructive">✱</span></Label>
                <Select
                  value={form.entity_type}
                  onValueChange={(v) => setField("entity_type", v)}
                >
                  <SelectTrigger data-testid="new-case-entity-type" className="rounded-sm h-11">
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((o) => (
                      <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="f-entity-name">Nombre de la entidad <span className="text-destructive">✱</span></Label>
                <Input
                  id="f-entity-name"
                  value={form.entity_name}
                  onChange={(e) => setField("entity_name", e.target.value)}
                  data-testid="new-case-entity-name"
                  className="rounded-sm h-11"
                  placeholder="Ej. Ministerio de Salud y Protección Social"
                />
                {form.entity_type === "nacional" && (
                  <p className="text-[11px] text-gray-500">
                    Marcada como entidad del orden nacional (por tipo seleccionado).
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Fuente de recursos <span className="text-destructive">✱</span></Label>
                <Select
                  value={form.funding_source}
                  onValueChange={(v) => setField("funding_source", v)}
                >
                  <SelectTrigger data-testid="new-case-funding" className="rounded-sm h-11">
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {FUNDING.map((o) => (
                      <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Sección 3 — Cifras y tiempos */}
          <section className="border border-gray-200 bg-white p-7">
            <SectionHeader n="3" title="Cifras y tiempos" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label htmlFor="f-amount">Valor estimado (COP) <span className="text-destructive">✱</span></Label>
                <Input
                  id="f-amount"
                  type="number"
                  min={0}
                  step={1}
                  value={form.estimated_amount_cop}
                  onChange={(e) => setField("estimated_amount_cop", e.target.value)}
                  data-testid="new-case-valor-input"
                  className="rounded-sm h-11"
                  placeholder="250000000"
                />
                {form.estimated_amount_cop && !Number.isNaN(valorNum) && valorNum > 0 && (
                  <p className="text-xs text-gray-500">{formatCOP(valorNum)}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="f-term">Plazo esperado (meses)</Label>
                <Input
                  id="f-term"
                  type="number"
                  min={0}
                  max={600}
                  value={form.expected_term_months}
                  onChange={(e) => setField("expected_term_months", e.target.value)}
                  data-testid="new-case-term-input"
                  className="rounded-sm h-11"
                  placeholder="Ej. 12"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="f-deadline">Fecha tope deseada (opcional)</Label>
                <Input
                  id="f-deadline"
                  type="date"
                  value={form.deadline_date}
                  onChange={(e) => setField("deadline_date", e.target.value)}
                  data-testid="new-case-deadline-input"
                  className="rounded-sm h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Urgencia</Label>
                <div className="flex gap-3 mt-2">
                  {URGENCIAS.map((u) => (
                    <label
                      key={u.v}
                      className={`flex-1 border px-3 py-2 cursor-pointer text-sm text-center transition-colors ${
                        form.urgencia === u.v
                          ? "border-primary bg-primary/5 text-primary font-medium"
                          : "border-gray-300 text-gray-700 hover:border-gray-400"
                      }`}
                    >
                      <input
                        type="radio"
                        name="urgencia"
                        value={u.v}
                        checked={form.urgencia === u.v}
                        onChange={() => setField("urgencia", u.v)}
                        className="sr-only"
                        data-testid={`urgencia-${u.v}`}
                      />
                      {u.l}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Sección 4 — Contexto */}
          <section className="border border-gray-200 bg-white p-7">
            <SectionHeader n="4" title="Contexto" subtitle="Campos opcionales que enriquecen la prevalidación." />

            <div className="grid grid-cols-1 gap-5">
              <div className="space-y-1.5">
                <Label>Departamento de ejecución</Label>
                <Select value={form.execution_department || undefined} onValueChange={(v) => setField("execution_department", v)}>
                  <SelectTrigger className="rounded-sm h-11" data-testid="new-case-department-select">
                    <SelectValue placeholder="Selecciona el departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTAMENTOS.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-gray-500">Se usa para buscar procesos comparables en SECOP del mismo territorio y de departamentos vecinos.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="f-loc">Ubicación de ejecución</Label>
                <Input
                  id="f-loc"
                  value={form.execution_location}
                  onChange={(e) => setField("execution_location", e.target.value)}
                  data-testid="new-case-location-input"
                  className="rounded-sm h-11"
                  placeholder="Ciudad / Departamento"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Códigos UNSPSC (de datos.gov.co)</Label>
                <UnspscPicker
                  value={form.unspsc_codes}
                  onChange={(codes) => setField("unspsc_codes", codes)}
                  max={5}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="f-links">Links a procesos SECOP similares (uno por línea)</Label>
                <Textarea
                  id="f-links"
                  rows={3}
                  value={form.similar_process_links}
                  onChange={(e) => setField("similar_process_links", e.target.value)}
                  data-testid="new-case-links-input"
                  className="rounded-sm"
                  placeholder="https://community.secop.gov.co/Public/Tendering/..."
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="f-riesgos">Riesgos percibidos</Label>
                <Textarea
                  id="f-riesgos"
                  rows={3}
                  value={form.riesgos_percibidos}
                  onChange={(e) => setField("riesgos_percibidos", e.target.value)}
                  data-testid="new-case-risks-input"
                  className="rounded-sm"
                  placeholder="¿Qué riesgos identifica el equipo del proceso?"
                />
              </div>
            </div>
          </section>

          {/* Sección 5 — Adjuntos (placeholder) */}
          <section className="border border-gray-200 bg-gray-50 p-7" data-testid="uploads-placeholder">
            <SectionHeader n="5" title="Adjuntos" subtitle="Próximamente." />
            <p className="text-xs text-gray-500">
              La carga de estudios previos, análisis del sector y soportes estará disponible en
              una próxima fase. Por ahora puedes describir lo más relevante en la sección de
              riesgos percibidos.
            </p>
          </section>

          {/* Sección 6 — Consentimiento */}
          <section className="border-2 border-primary/30 bg-primary/[0.02] p-7">
            <SectionHeader
              n="6"
              title="Consentimiento"
              subtitle="Obligatorio para correr la prevalidación."
            />
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={form.consent_terms}
                onCheckedChange={(c) => setField("consent_terms", Boolean(c))}
                data-testid="consent-checkbox"
                className="mt-1"
              />
              <span className="text-sm text-gray-700 leading-relaxed">
                Acepto los{" "}
                <Link to="/terminos" className="text-primary underline" target="_blank">
                  Términos del servicio
                </Link>{" "}
                y la{" "}
                <Link to="/politica-datos" className="text-primary underline" target="_blank">
                  Política de Tratamiento de Datos (Ley 1581 de 2012)
                </Link>{" "}
                para este caso específico. Entiendo que la prevalidación gratuita es un insumo
                automatizado de apoyo y <strong>no constituye asesoría jurídica concluyente</strong>.
              </span>
            </label>
          </section>

          {error && (
            <div
              data-testid="new-case-error"
              className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-4 py-3"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              className="rounded-sm border-gray-300 h-12"
              onClick={() => navigate("/dashboard")}
              data-testid="new-case-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-sm border-gray-300 h-12"
              disabled={!canSubmit || submitting}
              onClick={onSaveDraft}
              data-testid="new-case-save-draft-button"
            >
              {submitting ? "Guardando…" : "Guardar borrador"}
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit || submitting}
              className="rounded-sm h-12 px-8"
              data-testid="new-case-submit-button"
            >
              {submitting ? "Ejecutando prevalidación…" : "Guardar y prevalidar"}
            </Button>
          </div>

          <Disclaimer dataTestId="new-case-disclaimer" />
        </form>
      </div>
    </div>
  );
}
